import { ComponentConfig } from "@measured/puck";
import { Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ScoreSelectField from "../../fields/ScoreSelectField";
import VariationSelectField from "../../fields/VariationSelectField";
import TrajectoryChartblock, { TrajectoryChartblockProps } from "./TrajectoryChartBlock";

type ScoreSelection = {
    scoreId: number | null;
    variationId: string | null;
};

type ScoreSelectionFieldProps = {
    value: ScoreSelection | undefined;
    onChange: (value: ScoreSelection) => void;
};

const ScoreSelectionField = (props: ScoreSelectionFieldProps) => {
    const { value, onChange } = props;
    const theme = useTheme();

    const selection: ScoreSelection = value || {
        scoreId: null,
        variationId: null,
    };

    const update = (patch: Partial<ScoreSelection>) => {
        const next = { ...selection, ...patch };
        onChange(next);
    };

    return (
        <Stack spacing={theme.spacing(2)}>
            <ScoreSelectField
                value={selection.scoreId}
                onChange={(v) => {
                    update({ scoreId: v || null, variationId: null });
                }}
                field={{ type: "custom", render: () => <></> }}
                name="trajectoryChartblock.scoreId"
                id="trajectoryChartblock-score"
            />

            {selection.scoreId ?
                <VariationSelectField
                    scoreId={selection.scoreId}
                    value={selection.variationId}
                    onChange={(v) => update({ variationId: v })}
                    field={{ type: "custom", render: () => <></> }}
                    name="trajectoryChartblock.variationId"
                    id="trajectoryChartblock-variation"
                />
            :   <></>}
        </Stack>
    );
};

export type TrajectoryChartblockConfigProps = {
    scoreConfig?: {
        config?: ScoreSelection;
    };
    height?: number;
    showLegend?: "true" | "false";
};

export const TrajectoryChartblockConfig: ComponentConfig<TrajectoryChartblockConfigProps> = {
    label: "Trajectory Chart",
    fields: {
        scoreConfig: {
            type: "object",
            label: "Score",
            objectFields: {
                config: {
                    type: "custom",
                    render: (props: ScoreSelectionFieldProps) => (
                        <ScoreSelectionField value={props.value} onChange={props.onChange} />
                    ),
                },
            },
        },
        height: {
            type: "number",
            label: "Height (px)",
            min: 200,
        },
        showLegend: {
            type: "select",
            label: "Show legend",
            options: [
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ],
        },
    },
    defaultProps: {
        scoreConfig: {
            config: {
                scoreId: null,
                variationId: null,
            },
        },
        height: 420,
        showLegend: "true",
    },
    render: (props: TrajectoryChartblockConfigProps) => {
        const scoreId = props.scoreConfig?.config?.scoreId ?? null;
        const rawVariationId = props.scoreConfig?.config?.variationId ?? null;
        const variationId = rawVariationId ? Number(rawVariationId) : null;

        const renderProps: TrajectoryChartblockProps = {
            scoreId,
            variationId,
            height: props.height,
            showLegend: props.showLegend !== "false",
        };

        return <TrajectoryChartblock {...renderProps} />;
    },
};

export default TrajectoryChartblockConfig;
